import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getTeacherList } from 'app/redux/store/features/teachers'
import TeacherGridFilter from './components/TeacherGridFilter'
import TeacherGridData from './components/TeacherGridData'
import utilServices from '../../../services/util-services'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'

const TeachersGrid = () => {
    const navigate = useNavigate()
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { teachers, loading, error } = useSelector((state) => state.teachers)
    const { state } = useLocation()
    const [filter, setFilter] = useState(() =>
        state?.navigateFrom === 'cancel-add-teacher' ||
        state?.navigateFrom === 'cancel-update-teacher' ||
        state?.navigateFrom === 'add-teacher' ||
        state?.navigateFrom === 'update-teacher'
            ? { firstName: '', lastName: '', email: '', phoneNumber: '', sort: 'FirstName', isDescending: false }
            : { firstName: '', lastName: '', email: '', phoneNumber: '', sort: 'FirstName', isDescending: false }
    )

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        // Navigate from will be true when we come back from Add or update
        if (
            utilServices.isNullOrUndefined(state?.navigateFrom) ||
            !teachers ||
            !teachers.data ||
            teachers.data.length <= 0
        ) {
            setApiCallInProgress(true)
            dispatch(getTeacherList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [teacherGrid, setTeacherGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getTeacherList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getTeacherList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getTeacherList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getTeacherList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setTeacherGrid({ type: 'set-grid-filter', payload: value })
        },
        [setTeacherGrid]
    )

    const onAddTeacher = () => {
        navigate('/security/teachers/add')
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setTeacherGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setTeacherGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        fontSize={'large'}
                        onClick={onRefresh}
                        sx={{
                            mb: -1.2,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Teacher List
                </Typography>
            }
            sx={{ bgcolor: 'primary.main' }}
            headerSx={{
                height: 40,
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddTeacher}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                teacherGrid.filterIconColor && !teacherGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!teacherGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <TeacherGridFilter
                    mutation={applyFilter}
                    display={teacherGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <TeacherGridData
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default TeachersGrid
